/**
 * Snapshot builder — constructs an immutable `Snapshot` from gantt's live
 * state.
 *
 * Implements `05-` §C.1 with the Phase B decisions surfaced (D-11, D-23,
 * D-25, D-34). Per the Phase B plan, anchorOverrides remains empty (Phase
 * C wires the anchor-cache lifecycle).
 *
 * Phase B is analysis-only — `draggedTaskIds` is read for completeness but
 * not consumed by the analysis pipeline.
 */

import type {
  Snapshot,
  SchedulingConfig,
  TaskSnapshot,
  LinkSnapshot,
  TaskID,
  LinkID,
  NormalizedTaskID,
  TaskKind,
  LinkType,
  ConstraintType,
  TimeUnit,
  RawLinkType,
  AnchorOverride
} from "../core/types";
import { normalizeLinkId, normalizeTaskId, normalizeTaskIdSet } from "../core/ids";
import { GanttCalendarRegistry, type GanttCalendarBackend } from "./calendar_adapter";
import { GanttHierarchyView, type GanttHierarchyBackend } from "./hierarchy_adapter";

// ─────────────────────────────────────────────────────────────────────────────
// Structural gantt interface — minimal surface the snapshot builder needs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gantt-instance subset used by the snapshot builder. Other adapter pieces
 * (calendar, hierarchy) accept their own structural subsets — the union is
 * effectively a duck-typed gantt instance.
 */
export interface GanttSnapshotBackend extends GanttCalendarBackend, GanttHierarchyBackend {
  /** Returns array of links. */
  getLinks(): Array<{
    id: LinkID;
    source: TaskID;
    target: TaskID;
    type: RawLinkType;
    lag?: number;
  }>;
  /** State accessor — used for tasksDnd, group_mode, min_date, max_date. */
  getState(stateName?: string): {
    min_date?: Date;
    max_date?: Date;
    group_mode?: boolean | string;
    tasksDnd?: { drag_id?: TaskID };
    [k: string]: unknown;
  };
  /** Looks up gantt config.types.* for kind classification. */
  config: GanttCalendarBackend["config"] & GanttHierarchyBackend["config"] & {
    project_start?: Date;
    project_end?: Date;
    start_date?: Date;
    end_date?: Date;
    duration_unit?: TimeUnit;
    types?: { task?: string; project?: string; milestone?: string; placeholder?: string };
  };
  /** Reads task by id. */
  getTask(id: TaskID): {
    id: TaskID;
    parent?: TaskID;
    start_date?: Date;
    end_date?: Date;
    duration?: number;
    type?: string;
    constraint_type?: ConstraintType;
    constraint_date?: Date;
    calendar_id?: string | number;
    auto_scheduling?: boolean;
    unscheduled?: boolean;
    progress?: number;
    $keep_constraints?: boolean;
    [k: string]: unknown;
  };
  /**
   * Returns the child-derived span for a task — `{ start_date, end_date }`
   * where `start_date = min(descendant.start_date)` and
   * `end_date = max(descendant.end_date)`. Defined for both `project`-type
   * (summary) tasks and regular branches with children. v1's slack helper
   * uses this rather than the task's stored `start_date`/`end_date` for
   * summaries because gantt doesn't always keep those fields in sync with
   * children.
   */
  getSubtaskDates?(taskId: TaskID): { start_date?: Date; end_date?: Date } | null;
  /** Reads the normalized auto-scheduling config object. */
  _getAutoSchedulingConfig?(): Partial<SchedulingConfig> & {
    enabled?: boolean;
    apply_constraints?: boolean;
    gap_behavior?: "preserve" | "compress";
    descendant_links?: boolean;
    schedule_on_parse?: boolean;
    move_projects?: boolean;
    use_progress?: boolean;
    schedule_from_end?: boolean;
    project_constraint?: boolean;
    show_constraints?: boolean;
    [k: string]: unknown;
  };

  getTaskType?(task: any): string;
  isSummaryTask(task: any): boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function classifyTask(task: any, gantt: GanttSnapshotBackend): TaskKind {
  if(gantt.isSummaryTask(task)){
    return "summary";
  }

  const typeStr = gantt.getTaskType ? gantt.getTaskType(task) : task.type;
  const types = gantt.config.types ?? {};
  if (typeStr === undefined) return "leaf";
  if (typeStr === types.project) return "summary";
  if (typeStr === types.milestone) return "milestone";
  if (typeStr === types.placeholder) return "placeholder";
  return "leaf";
}

function normalizeLinkType(raw: RawLinkType): LinkType {
  const s = String(raw);
  switch (s) {
    case "0": case "fs": return "fs";
    case "1": case "ss": return "ss";
    case "2": case "ff": return "ff";
    case "3": case "sf": return "sf";
    default: return "fs"; // defensive
  }
}

function resolveProjectAnchors(
  gantt: GanttSnapshotBackend,
  tasks: ReadonlyMap<NormalizedTaskID, TaskSnapshot>
): { projectStart: Date; projectEnd: Date } {
  // Forward chain: explicit config → actual task min/max → gantt's rendered
  // bounds → now. The "actual min/max" tier prefers task-derived dates over
  // `state.{min,max}_date` because gantt's timeline state can pad past the
  // last real task (e.g., rounding to scale boundaries). v1's slack helper
  // uses `gantt.getSubtaskDates().end_date` directly, which is equivalent.
  // Including `state.{min,max}_date` as the next fallback because it carries
  // a meaningful upper bound when explicit `project_end` is unset AND the
  // task map is empty/anomalous.
  //
  // Exclude derived (child-aggregated) summaries from the actual-min/max
  // computation. Their `startDate`/`endDate` fall back to `new Date()`
  // when gantt didn't populate child-derived dates before snapshot build —
  // including "now" in the project window would push the horizon years
  // past the actual schedule and produce spurious slack across the chain.
  const state = gantt.getState();
  const cfg = gantt.config;

  let earliestStart = Infinity;
  let latestEnd = -Infinity;
  for (const t of tasks.values()) {
    // Skip derived summaries (project + auto_scheduling !== false) —
    // matches v1's `isSummaryTask && !isManuallyScheduledSummary` test
    // and the `AnalysisNode.derivedFromChildren` flag.
    if (t.kind === "summary" && t.autoScheduling !== false) continue;
    const s = t.startDate.getTime();
    const e = t.endDate.getTime();
    if (s < earliestStart) earliestStart = s;
    if (e > latestEnd) latestEnd = e;
  }

  const projectStart =
    cfg.project_start ??
    cfg.start_date ??
    (Number.isFinite(earliestStart) ? new Date(earliestStart) : (state.min_date ?? new Date()));

  const projectEnd =
    cfg.project_end ??
    cfg.end_date ??
    (Number.isFinite(latestEnd) ? new Date(latestEnd) : (state.max_date ?? new Date()));

  return { projectStart, projectEnd };
}

function mapAutoSchedulingConfig(
  gantt: GanttSnapshotBackend,
  groupMode: boolean
): SchedulingConfig {
  const raw = gantt._getAutoSchedulingConfig
    ? gantt._getAutoSchedulingConfig()
    : {};

  const draggedTaskIds: Set<NormalizedTaskID> | undefined = (() => {
    const dnd = gantt.getState("tasksDnd") as { drag_id?: TaskID } | undefined;
    if (dnd?.drag_id !== undefined && dnd.drag_id !== null) {
      return new Set<NormalizedTaskID>([normalizeTaskId(dnd.drag_id)]);
    }
    return undefined;
  })();

  return {
    enabled: raw.enabled ?? false,
    applyConstraints: raw.apply_constraints ?? false,
    scheduleFromEnd: raw.schedule_from_end ?? false,
    moveProjects: raw.move_projects ?? true,
    gapBehavior: raw.gap_behavior ?? "preserve",
    projectConstraint: raw.project_constraint ?? false,
    groupMode,
    useProgress: raw.use_progress ?? false,
    descendantLinks: raw.descendant_links ?? false,
    inheritCalendar: gantt.config.inherit_calendar ?? false,
    scheduleOnParse: raw.schedule_on_parse ?? true,
    strictCalendar: (raw.strict_calendar as boolean | undefined) ?? false, // D-A: lenient default
    maxIterations: typeof raw.maxIterations === "number" ? raw.maxIterations : 8, // §16 default
    draggedTaskIds
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// buildSnapshot
// ─────────────────────────────────────────────────────────────────────────────

export interface BuildSnapshotOptions {
  /** Optional scope — Phase C uses this for rootTaskId-bounded planning. */
  rootTaskId?: NormalizedTaskID;
  /** Optional pre-mutation anchor overrides (Phase C wires the anchor cache). */
  anchorOverrides?: ReadonlyMap<NormalizedTaskID, AnchorOverride>;
  /**
   * Per D-01 two-phase plan flow (event_bridge.ts): tasks listed here are
   * forced to `autoScheduling: false` in the produced TaskSnapshot. The
   * second `plan()` pass after a per-task `onBeforeTaskAutoSchedule`
   * cancellation thus treats cancelled tasks as fixed-date nodes — their
   * successors schedule against the unchanged anchor, matching v1's
   * "cancelled tasks don't enter plansHash" semantics.
   */
  cancelledTaskIds?: ReadonlySet<TaskID>;
}

export function buildSnapshot(
  gantt: GanttSnapshotBackend,
  options: BuildSnapshotOptions = {}
): Snapshot {
  const durationUnit: TimeUnit = (gantt.config.duration_unit as TimeUnit) ?? "day";
  const rootId = normalizeTaskId(gantt.config.root_id ?? 0);
  const cancelledTaskIds = normalizeTaskIdSet(options.cancelledTaskIds);

  // ── Pass 1: build TaskSnapshot map; apply D-11 unscheduled filter. ─
  const tasks = new Map<NormalizedTaskID, TaskSnapshot>();
  gantt.eachTask((rawTask) => {
    const rawTaskId = rawTask.id;
    const taskId = normalizeTaskId(rawTaskId);
    const t = gantt.getTask(rawTaskId);
    // D-11: filter unscheduled tasks at snapshot build.
    if (t.unscheduled) return;

    const kind = classifyTask(t, gantt);
    if (kind === "placeholder") return; // §11.5 placeholder exclusion

    if (t.$virtual) return; // exclude group tasks

    // For derived summaries (project type WITHOUT `auto_scheduling: false`),
    // prefer the live child-derived span over `t.start_date` / `t.end_date`.
    // Gantt only writes the derived span back to the task fields on certain
    // mutation paths (`resetProjectDates`), so the stored value can be
    // out-of-sync with the actual children — typically falling back to
    // `gantt.config.start_date` for new instances. v1's slack helper calls
    // `getSubtaskDates()` on demand for exactly this reason
    // (`ext/critical_path/slack.js:38`). Without this, D-08 summary
    // translation computes `delta = newAnchor - storedAnchor` against a
    // non-canonical anchor and moves descendants by garbage offsets.
    //
    // Manually-scheduled summaries (`auto_scheduling: false`) carry their
    // own dates intentionally per docs/guides/custom-projects-dates.md and
    // skip the override.
    let startSrc: Date;
    let endSrc: Date;
    const isDerivedSummary = kind === "summary" && t.auto_scheduling !== false;
    if (isDerivedSummary && gantt.getSubtaskDates) {
      const sub = gantt.getSubtaskDates(rawTaskId);
      startSrc = sub?.start_date ?? t.start_date ?? new Date();
      endSrc = sub?.end_date ?? t.end_date ?? new Date();
    } else {
      startSrc = t.start_date ?? new Date();
      endSrc = t.end_date ?? new Date();
    }

    // D-01: cancelled tasks become fixed-date nodes in the second plan() pass.
    const isCancelled = cancelledTaskIds?.has(taskId) === true;
    const rawParent = t.parent ?? null;
    const parent = rawParent === null || normalizeTaskId(rawParent) === rootId
      ? null
      : normalizeTaskId(rawParent);

    const snap: TaskSnapshot = {
      id: taskId,
      rawId: rawTaskId,
      kind,
      parent,
      rawParent,
      // D-34: clone Date instances — engine treats as immutable.
      startDate: new Date(startSrc.getTime()),
      endDate: new Date(endSrc.getTime()),
      duration: t.duration ?? 0,
      durationUnit,
      constraintType: t.constraint_type,
      constraintDate: t.constraint_date ? new Date(t.constraint_date.getTime()) : undefined,
      autoScheduling: isCancelled ? false : t.auto_scheduling !== false,
      unscheduled: false, // already filtered
      progress: typeof t.progress === "number" ? t.progress : 0,
      keepConstraints: t.$keep_constraints === true
    };
    tasks.set(taskId, snap);
  });

  // ── Hierarchy + calendars ────────────────────────────────────────────
  const includedIds = new Set<NormalizedTaskID>(tasks.keys());
  const hierarchy = new GanttHierarchyView(gantt, includedIds);
  const calendars = new GanttCalendarRegistry(gantt, hierarchy);

  // ── Config ──────────────────────────────────────────────────────────
  // group_mode signal per D-24. Gantt stores as boolean or grouping-spec
  // string; treat anything truthy as "grouping is active".
  const state = gantt.getState();
  const groupMode = Boolean(state.group_mode);
  const config = mapAutoSchedulingConfig(gantt, groupMode);

  // ── Pass 2: build LinkSnapshot[] with D-25 descendant-link filter ──
  //
  // Gantt accepts both string and number IDs in user data — internal tasks
  // store the type the user provided, but link.source / link.target may be
  // the other form (e.g., user wrote `{source: "1", target: "2"}` while
  // `{id: 1}` on the tasks). The downstream engine uses `Map<TaskID, …>`
  // lookups which require exact type+value match, so we normalize each
  // link's source/target to the actual task's ID before storing.
  const rawLinks = gantt.getLinks();
  const links: LinkSnapshot[] = [];
  const filterDescendantLinks = config.descendantLinks === false;

  function resolveTaskId(rawId: unknown): NormalizedTaskID | undefined {
    if (rawId === undefined || rawId === null) return undefined;
    const id = normalizeTaskId(rawId as TaskID);
    return tasks.has(id) ? id : undefined;
  }

  for (const l of rawLinks) {
    const src = resolveTaskId(l.source);
    const tgt = resolveTaskId(l.target);
    if (src === undefined || tgt === undefined) continue;
    if (filterDescendantLinks) {
      // D-25 (v1 parity — links_common.js:66 / ui_handlers `_preventDescendantLink`):
      // only a link between a SUMMARY and its own descendant is "inherited"
      // and dropped. A summary derives its dates from its children, so such a
      // link is effectively a cycle. A regular (leaf) branch task — a
      // `type:"task"` node that carries its own start/duration but happens to
      // have subtasks — has independent timing, so its links to descendants
      // behave like any other link and must be kept. The ancestor endpoint is
      // the one that must be a summary for the drop to apply.
      if (hierarchy.isDescendantOf(tgt, src) && hierarchy.isSummary(src)) continue; // summary src → descendant tgt
      if (hierarchy.isDescendantOf(src, tgt) && hierarchy.isSummary(tgt)) continue; // descendant src → summary tgt
    }
    links.push({
      id: normalizeLinkId(l.id),
      rawId: l.id,
      source: src,
      rawSource: l.source,
      target: tgt,
      rawTarget: l.target,
      type: normalizeLinkType(l.type),
      lag: typeof l.lag === "number" ? l.lag : 0,
      lagUnit: durationUnit // gantt stores lag as a number in config.duration_unit
    });
  }

  // ── Project anchors per D-23 fallback chain ──────────────────────────
  const { projectStart, projectEnd } = resolveProjectAnchors(gantt, tasks);

  // ── Anchor overrides ─────────────────────────────────────────────────
  // Phase B: empty unless caller passes them in (Phase C wires the cache).
  const anchorOverrides = options.anchorOverrides ?? new Map<NormalizedTaskID, AnchorOverride>();

  const explicitProjectStart = gantt.config.project_start || undefined;
  const explicitProjectEnd = gantt.config.project_end || undefined;

  return {
    tasks,
    links,
    calendars,
    hierarchy,
    config,
    projectStart,
    explicitProjectStart,
    projectEnd,
    explicitProjectEnd,
    anchorOverrides
  };
}
